import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link, Text } from 'core/components';
import $syn from 'app/stores/synthetics/$syn';
import AgentRenderer from 'app/views/synthetics/components/AgentRenderer';
import { AgentCollection, AgentModel } from 'app/stores/synthetics/synthTypes';

interface SynthAgentProps {
  /** The synthetic agent ID */
  agentId: string | number;
  /** Show location info below the name */
  showLocation?: boolean;
  /** Text size */
  small?: boolean;
  /** Whether to make the agent name clickable (links to agent detail page) */
  clickable?: boolean;
}

/**
 * Renders a synthetic agent name by looking up the agent from the $syn store.
 * Uses the existing AgentRenderer component for consistent display.
 */
function SynthAgent({ agentId, showLocation = false, small = false, clickable = true }: SynthAgentProps) {
  // Access agents from store inside component (not at module load time)
  const synAgents = $syn.agents as AgentCollection | null;

  // Ensure agents are fetched once - rely on hasFetched to prevent duplicate fetches
  useEffect(() => {
    if (synAgents && !synAgents.hasFetched) {
      synAgents.fetch();
    }
  }, [synAgents]);

  if (!agentId || !synAgents) {
    return (
      <Text small={small} muted>
        --
      </Text>
    );
  }

  // Get the agent (get() can return Model | Model[], so narrow the type)
  const agentResult = synAgents?.get(agentId);
  const agent: AgentModel | undefined = Array.isArray(agentResult) ? agentResult[0] : agentResult;

  // Access allAgents to ensure MobX observes the collection and re-renders when agents are loaded
  const { hasFetched, allAgents } = synAgents;

  if (!agent) {
    // Agent not found - show loading if fetch is in progress, otherwise show ID as fallback
    if (!hasFetched || allAgents.length === 0) {
      return (
        <Text small={small} muted>
          Loading Agent: {agentId} ...
        </Text>
      );
    }
    return (
      <Text small={small} muted>
        Agent {agentId}
      </Text>
    );
  }

  const agentContent = (
    <AgentRenderer agent={agent} locationInfo={agent.locationInfo} showLocationInfo={showLocation} small={small} />
  );

  // Link directly to the agent detail page
  if (clickable) {
    const agentDetailUrl = `/v4/synthetics/agents/${agentId}/summary`;
    return (
      <Link to={agentDetailUrl} aria-label="View agent details (opens in new tab)" blank rel="noopener noreferrer">
        {agentContent}
      </Link>
    );
  }

  return agentContent;
}

export default observer(SynthAgent);
